// components/Comments/CommentItem.jsx
import { useState } from 'react';
import {
  Paper, Stack, Box, Typography, TextField,
  Button, Chip, Collapse
} from '@mui/material';
import CommentAction from './CommentAction';
import { useUser } from '../../contexts/UserContext';
import UserNicknameWithEmoticon from '../Common/UserNicknameWithEmoticon';
import AuthorLink from '../Common/AuthorLink';

const BG = ['#f9f9f9', '#f2f2f2'];
const LINE_COLOR = 'grey.300';

export default function CommentItem({
  comment, onUpdate, onDelete,
  onReply, onReport, onLike,
  depth = 0,
  postAuthorId,
}) {
  const { userInfo } = useUser();
  const [isEditing, setIsEditing] = useState(false);
  const [editContent, setEditContent] = useState(comment.content);
  const [replyContent, setReplyContent] = useState('');
  const [showReply, setShowReply] = useState(false);
  const [likeLoading, setLikeLoading] = useState(false);

  const isDeleted = comment.is_deleted;
  const isHidden = comment.is_hidden;
  const isAdmin = userInfo?.is_admin;

  const isAdminOrMaster =
    userInfo?.role === 'admin' ||
    userInfo?.role === 'master' ||
    userInfo?.is_admin;

  // ✅ 작성자 라인 렌더링 함수로 분리
  const renderAuthorLine = () => {
    const baseNickname =
      comment.nickname ||
      comment.author_username ||
      '알 수 없음';

    const sanitizedNickname = baseNickname;

    const nicknameFragment = comment.is_anonymous ? (
      <Typography component="span" variant="subtitle2" fontWeight="bold">
        {isAdminOrMaster
          ? (sanitizedNickname.startsWith('익명(')
            ? sanitizedNickname          // 예: 익명(쿠야) 이미 있으면 그대로
            : `익명(${sanitizedNickname})`)
          : '익명'}
      </Typography>
    ) : (
      <AuthorLink
        nickname={sanitizedNickname}
        disabled={false}
        sx={{ fontWeight: 'bold', textDecoration: 'none' }}
      />
    );

    return (
      <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap">
        <UserNicknameWithEmoticon
          username=""
          usernameComponent={nicknameFragment}
          mainTitle={comment.is_anonymous ? null : comment.main_title}
          subTitle={comment.is_anonymous ? null : comment.sub_title}
          emoticonUrl={comment.is_anonymous ? null : comment.equipped_emoticon}
          emojiText={comment.is_anonymous ? null : comment.equipped_emoticon_text}
          size={30}
        />
        {comment.author_id === postAuthorId && (
          <Typography
            variant="caption"
            color="text.secondary"
            sx={{ fontWeight: 'bold', fontSize: '0.85rem' }}
          >
            (작성자)
          </Typography>
        )}
      </Stack>
    );
  };

  /* 좋아요 */
  const handleLike = async (id) => {
    setLikeLoading(true);
    try {
      await onLike(id);
    } finally {
      setLikeLoading(false);
    }
  };

  /* 수정 저장 */
  const saveEdit = () => {
    if (!editContent.trim()) return alert('내용을 입력하세요!');
    onUpdate(comment.id, editContent);
    setIsEditing(false);
  };

  /* 답글 작성 */
  const submitReply = () => {
    if (!replyContent.trim()) return alert('내용을 입력하세요!');
    onReply(comment.id, replyContent);
    setReplyContent('');
    setShowReply(false);
  };

  return (
    <Paper
      id={`comment-${comment.id}`}
      elevation={0}
      sx={{
        width: '100%',
        p: { xs: 1.25, sm: 1.5 },
        mb: 1.5,
        ml: { xs: depth * 2, sm: depth * 4 },
        bgcolor: BG[depth] ?? BG[0],
        position: 'relative',
        '::before': depth > 0 && {
          content: '""',
          position: 'absolute',
          left: -8,
          top: 0,
          bottom: 0,
          width: 2,
          bgcolor: LINE_COLOR,
        },
        '::after': depth === 1 && {
          content: '""',
          position: 'absolute',
          width: 12,
          height: 12,
          left: -10,
          top: 6,
          borderLeft: `2px solid ${LINE_COLOR}`,
          borderBottom: `2px solid ${LINE_COLOR}`,
        },
      }}
    >
      {/* 작성자 + 날짜 → 1줄(작성자), 2줄(날짜) */}
      <Box sx={{ mb: 0.5 }}>
        {/* ① 작성자 정보 라인 */}
        {renderAuthorLine()}

        {/* ② 작성일 라인 */}
        <Typography
          variant="caption"
          color="text.secondary"
          sx={{
            mt: 0.25,
            fontSize: { xs: '0.7rem', sm: '0.8rem' },
            textAlign: 'left',
            display: 'block',
          }}
        >
          {new Date(comment.created_at).toLocaleString('ko-KR', {
            year: 'numeric',
            month: '2-digit',
            day: '2-digit',
            hour: '2-digit',
            minute: '2-digit',
          })}
          {comment.is_edited && ' (수정됨)'}
        </Typography>
      </Box>

      {/* 본문 또는 상태 */}
      {isDeleted ? (
        <Chip label="삭제된 댓글" size="small" variant="outlined" sx={{ mt: 1 }} />
      ) : isHidden && !isAdmin ? (
        <Chip label="블라인드된 댓글" size="small" variant="outlined" sx={{ mt: 1 }} />
      ) : isEditing ? (
        <Box sx={{ mt: 1 }}>
          <TextField
            fullWidth
            multiline
            minRows={2}
            value={editContent}
            onChange={(e) => setEditContent(e.target.value)}
          />
          <Stack direction="row" spacing={1} sx={{ mt: 1 }}>
            <Button onClick={saveEdit}>저장</Button>
            <Button onClick={() => setIsEditing(false)}>취소</Button>
          </Stack>
        </Box>
      ) : (
        <Typography
          sx={{
            mt: 1,
            whiteSpace: 'pre-wrap',
            textAlign: 'left',
            fontSize: { xs: '0.9rem', sm: '1rem' },
          }}
        >
          {comment.content}
        </Typography>
      )}

      {/* 액션 툴바 */}
      {!isDeleted && (!isHidden || isAdmin) && (
        <Box sx={{ mt: 1 }}>
          <CommentAction
            comment={comment}
            onEdit={() => setIsEditing(true)}
            onDelete={onDelete}
            onReply={() => setShowReply((prev) => !prev)}
            onReport={onReport}
            onLike={handleLike}
            isEditing={isEditing}
            showReplyForm={showReply}
            depth={depth}
            loading={likeLoading}
          />
        </Box>
      )}

      {/* 답글 폼 */}
      {depth === 0 && (
        <Collapse
          in={showReply && !isDeleted && (!isHidden || isAdmin)}
          unmountOnExit
        >
          <Box sx={{ mt: 1 }}>
            <TextField
              fullWidth
              multiline
              minRows={2}
              value={replyContent}
              onChange={(e) => setReplyContent(e.target.value)}
              placeholder="답글을 입력하세요..."
              sx={{ fontSize: { xs: '0.9rem', sm: '1rem' } }}
              onKeyDown={(e) => {
                if (e.key === 'Enter' && !e.shiftKey) {
                  e.preventDefault();
                  submitReply();
                }
              }}
            />
            <Button
              size="small"
              onClick={submitReply}
              sx={{ mt: 0.5, fontSize: { xs: '0.75rem', sm: '0.85rem' } }}
            >
              작성
            </Button>
          </Box>
        </Collapse>
      )}

      {/* 대댓글 렌더링 */}
      {depth === 0 &&
        comment.replies?.map((r) => (
          <CommentItem
            key={r.id}
            comment={r}
            postAuthorId={postAuthorId}
            onUpdate={onUpdate}
            onDelete={onDelete}
            onReply={onReply}
            onReport={onReport}
            onLike={onLike}
            depth={1}
          />
        ))}
    </Paper>
  );
}
